/**
 * StoresTab — Locales del punto de venta.
 */
import { useEffect, useState } from 'react'
import { Plus, Pencil } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { usePermission } from '@/hooks/usePermission'
import { useToast, ToastContainer } from '@/components/Toast'
import { Button } from '@/components/Button'
import { DataTable } from '@/components/ds/DataTable'
import { Modal } from '@/components/ds/Modal'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { StatusBadge } from '@/components/ds/StatusBadge'
import { EmptyState } from '@/components/EmptyState'
import { extractErrorMessage, extractFieldErrors } from '@/lib/errorUtils'
import { useWarehouses } from '@/modules/inventory/hooks/useInventory'
import { usePosStores, useSavePosStore, type PosStore, type OversellPolicy } from '../hooks/usePos'

const OVERSELL_LABELS: Record<OversellPolicy, string> = {
  block: 'Bloquear la venta',
  warn: 'Avisar y permitir',
  allow: 'Permitir sin avisar',
}

export default function StoresTab() {
  const { can } = usePermission()
  const toast = useToast()
  const { data: stores = [], isLoading } = usePosStores()
  const [editing, setEditing] = useState<Partial<PosStore> | null>(null)

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-content-muted">{stores.length} locales</span>
        {can('pos.stores', 'create') && (
          <Button onClick={() => setEditing({ oversell_policy: 'warn', is_active: true })}>
            <Plus size={14} /> Nuevo local
          </Button>
        )}
      </div>

      <DataTable<PosStore>
        rows={stores}
        rowKey={(s) => s.id}
        loading={isLoading}
        empty={<EmptyState title="No hay locales." hint="Cada caja del POS pertenece a un local, y el local define la bodega de la que descuenta." size="sm" />}
        columns={[
          { key: 'code', header: 'Código', render: (s) => <span className="font-mono">{s.code}</span> },
          { key: 'name', header: 'Nombre' },
          { key: 'warehouse', header: 'Bodega', render: (s) => `${s.warehouse_code} · ${s.warehouse_name}` },
          { key: 'oversell', header: 'Venta sin stock', render: (s) => OVERSELL_LABELS[s.oversell_policy] },
          { key: 'terminals', header: 'Terminales', numeric: true, render: (s) => s.terminals_count },
          {
            key: 'status', header: 'Estado',
            render: (s) => (
              <StatusBadge status={s.is_active ? 'active' : 'inactive'} label={s.is_active ? 'Activo' : 'Desactivado'}
                tone={s.is_active ? 'success' : 'neutral'} />
            ),
          },
          {
            key: 'actions', header: '',
            render: (s) => can('pos.stores', 'edit') && (
              <button onClick={() => setEditing(s)} title="Editar" aria-label={`Editar ${s.name}`}
                className="p-1.5 rounded-lg hover:bg-soft text-content-subtle hover:text-content-muted">
                <Pencil size={14} />
              </button>
            ),
          },
        ]}
      />

      {editing && (
        <StoreModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={(nuevo) => {
            toast.success(nuevo ? 'Local creado' : 'Local actualizado')
            setEditing(null)
          }}
        />
      )}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

function StoreModal({ initial, onClose, onSaved }: {
  initial: Partial<PosStore>
  onClose: () => void
  onSaved: (nuevo: boolean) => void
}) {
  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm<Partial<PosStore>>()
  const { data: bodegas } = useWarehouses({ active: 'true', page_size: 200 })
  const { mutate, isPending } = useSavePosStore()
  const [error, setGeneral] = useState<string | null>(null)

  // Se carga cuando ya están las bodegas: antes, el select no tiene la opción y
  // queda en el placeholder, y guardar pedía la bodega de nuevo.
  useEffect(() => { if (bodegas) reset(initial) }, [initial, reset, bodegas])

  const onSubmit = (data: Partial<PosStore>) => {
    setGeneral(null)
    mutate({ ...data, id: initial.id, tip_suggestion_pct: data.tip_suggestion_pct || null }, {
      onSuccess: () => onSaved(!initial.id),
      onError: (e) => {
        const campos = extractFieldErrors(e)
        if (campos) Object.entries(campos).forEach(([k, m]) => setError(k as keyof PosStore, { message: m }))
        setGeneral(extractErrorMessage(e, 'No se pudo guardar el local.'))
      },
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={initial.id ? 'Editar local' : 'Nuevo local'}
      size="lg"
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="pos-store-form" isPending={isPending}>Guardar</Button>
        </>
      }
    >
      <form id="pos-store-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField dense label="Código" required placeholder="LOC-01"
            inputProps={register('code', { required: 'El código es obligatorio.' })} error={errors.code?.message} />
          <TextField dense label="Nombre" required placeholder="Local Centro"
            inputProps={register('name', { required: 'El nombre es obligatorio.' })} error={errors.name?.message} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
          <TextField dense label="Dirección" inputProps={register('address')} error={errors.address?.message} />
          <TextField dense label="Comuna" placeholder="Providencia"
            inputProps={register('commune')} error={errors.commune?.message} />
        </div>
        <TextField dense label="Código de sucursal SII" placeholder="Vacío si el local es la casa matriz"
          hint="El que el SII asignó a esta sucursal. Va en el comprobante impreso y en las facturas que emite el local."
          inputProps={{ ...register('sii_branch_code', { setValueAs: (v) => (v === '' || v == null ? null : Number(v)) }),
            inputMode: 'numeric' }}
          error={errors.sii_branch_code?.message} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField dense label="Bodega" required placeholder="Seleccionar la bodega"
            options={(bodegas?.warehouses || []).map((w) => ({ value: w.id, label: `${w.code} · ${w.name}` }))}
            selectProps={register('warehouse', { required: 'La bodega es obligatoria.' })}
            error={errors.warehouse?.message} />
          <SelectField dense label="Venta sin stock"
            options={Object.entries(OVERSELL_LABELS).map(([value, label]) => ({ value, label }))}
            selectProps={register('oversell_policy')} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField dense label="Zona horaria" placeholder="La del país de la empresa"
            hint="Formato IANA, p. ej. America/Santiago. Vacía usa la del país."
            inputProps={register('timezone')} error={errors.timezone?.message} />
          <TextField dense label="Propina sugerida (%)" placeholder="10"
            inputProps={register('tip_suggestion_pct')} error={errors.tip_suggestion_pct?.message} />
        </div>
        {initial.id && (
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" {...register('is_active')} />
            Activo. Desactivarlo deja a todas sus cajas sin poder operar.
          </label>
        )}
        {error && <p className="text-sm text-danger-content">{error}</p>}
      </form>
    </Modal>
  )
}
